import React, { useState, useEffect } from 'react';
import axiosInstance from '../../lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Eye, Users, MousePointerClick, TrendingDown, ArrowRight,
  Monitor, Smartphone, Tablet, Globe, Search, Share2, ExternalLink, Activity
} from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'sonner';

export default function AdminPageAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchAll();
  }, [days]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get(`${API_URL}/api/admin/page-analytics`, { params: { days } });
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load page analytics');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n) => (Number(n) || 0).toLocaleString('en-IN');

  const deviceIcon = (d) => d === 'mobile' ? Smartphone : d === 'tablet' ? Tablet : Monitor;
  const referrerIcon = (r) => {
    if (r === 'google' || r === 'search') return Search;
    if (r === 'social') return Share2;
    if (r === 'internal') return Activity;
    if (r === 'direct') return Globe;
    return ExternalLink;
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-page-analytics">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Page Analytics</h1>
            <p className="text-gray-600 mt-1">
              Who is visiting your site, which pages they land on, and where they drop off
            </p>
          </div>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-4 py-2 border border-gray-300 rounded-lg bg-white"
            data-testid="page-analytics-period"
          >
            <option value={1}>Last 24 hours</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" />
          </div>
        ) : !data ? (
          <div className="text-center py-16 text-gray-500">No traffic data yet</div>
        ) : data.total_pageviews === 0 ? (
          <Card>
            <CardContent className="p-10 text-center">
              <Activity className="w-14 h-14 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No traffic captured yet</h3>
              <p className="text-gray-600 max-w-md mx-auto mb-4">
                Native page tracking is now active. Once visitors land on your site, data will appear
                here in real time. Try opening your storefront in an incognito window to verify.
              </p>
              <a
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-emerald-600 hover:underline"
              >
                Visit storefront <ExternalLink className="w-4 h-4" />
              </a>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Headline stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <StatCard
                title="Page Views"
                value={fmt(data.total_pageviews)}
                icon={Eye}
                color="text-blue-600"
                bg="bg-blue-50"
              />
              <StatCard
                title="Unique Visitors"
                value={fmt(data.unique_visitors)}
                icon={Users}
                color="text-emerald-600"
                bg="bg-emerald-50"
              />
              <StatCard
                title="Sessions"
                value={fmt(data.unique_sessions)}
                icon={MousePointerClick}
                color="text-purple-600"
                bg="bg-purple-50"
              />
              <StatCard
                title="Bounce Rate"
                value={`${data.bounce_rate_pct}%`}
                icon={TrendingDown}
                color={data.bounce_rate_pct > 60 ? 'text-red-600' : 'text-amber-600'}
                bg={data.bounce_rate_pct > 60 ? 'bg-red-50' : 'bg-amber-50'}
              />
              <StatCard
                title="Pages / Session"
                value={data.avg_pages_per_session}
                icon={Activity}
                color="text-slate-700"
                bg="bg-slate-50"
              />
            </div>

            {/* Daily traffic chart */}
            {data.daily && data.daily.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Daily Traffic</CardTitle>
                </CardHeader>
                <CardContent>
                  <TrafficChart data={data.daily} />
                </CardContent>
              </Card>
            )}

            {/* Funnel + Devices + Referrers */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Funnel */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-base">Conversion Funnel</CardTitle>
                </CardHeader>
                <CardContent>
                  <Funnel funnel={data.funnel} />
                </CardContent>
              </Card>

              {/* Devices */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Devices</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {data.devices.map((d) => {
                      const Icon = deviceIcon(d.device);
                      const total = data.devices.reduce((s, x) => s + x.count, 0);
                      const pct = total ? Math.round((d.count / total) * 100) : 0;
                      return (
                        <div key={d.device}>
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="flex items-center gap-2 text-gray-700 capitalize">
                              <Icon className="w-4 h-4" /> {d.device}
                            </span>
                            <span className="font-medium">{pct}% ({fmt(d.count)})</span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-emerald-500"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Referrers */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Where Visitors Came From</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {data.referrers.map((r) => {
                    const Icon = referrerIcon(r.source);
                    return (
                      <div
                        key={r.source}
                        className="p-4 rounded-xl bg-gray-50 text-center"
                      >
                        <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center mx-auto mb-2 shadow-sm">
                          <Icon className="w-5 h-5 text-emerald-600" />
                        </div>
                        <p className="text-xs text-gray-500 capitalize">{r.source}</p>
                        <p className="text-lg font-bold mt-1">{fmt(r.count)}</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Top Pages */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Top Pages</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b">
                      <tr className="text-left text-gray-500">
                        <th className="py-2 pr-4 font-medium">Path</th>
                        <th className="py-2 pr-4 font-medium">Title</th>
                        <th className="py-2 pr-4 font-medium text-right">Views</th>
                        <th className="py-2 font-medium text-right">Unique</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {data.top_pages.map((p) => (
                        <tr key={p.path} className="hover:bg-gray-50">
                          <td className="py-2 pr-4">
                            <a
                              href={p.path}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-emerald-700 hover:underline font-mono text-xs"
                            >
                              {p.path}
                            </a>
                          </td>
                          <td className="py-2 pr-4 text-gray-600 truncate max-w-xs">
                            {p.title || '—'}
                          </td>
                          <td className="py-2 pr-4 text-right font-medium">{fmt(p.views)}</td>
                          <td className="py-2 text-right text-gray-600">{fmt(p.unique_visitors)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function StatCard({ title, value, icon: Icon, color, bg }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">{title}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          </div>
          <div className={`w-11 h-11 ${bg} rounded-xl flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Funnel({ funnel }) {
  const labels = {
    home: 'Home',
    products_list: 'Products List',
    product_detail: 'Product Detail',
    cart: 'Cart',
    checkout: 'Checkout',
    order: 'Order Placed',
  };
  const max = Math.max(...funnel.map((s) => s.sessions), 1);
  return (
    <div className="space-y-2">
      {funnel.map((step, idx) => {
        const prev = idx > 0 ? funnel[idx - 1].sessions : null;
        const dropPct = prev && prev > 0 ? Math.round((1 - step.sessions / prev) * 100) : null;
        const widthPct = (step.sessions / max) * 100;
        return (
          <div key={step.step}>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="flex items-center gap-2 text-gray-700">
                <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 font-bold text-xs flex items-center justify-center">
                  {idx + 1}
                </span>
                {labels[step.step] || step.step}
              </span>
              <span className="flex items-center gap-3">
                {dropPct !== null && dropPct > 0 && (
                  <span className="text-red-500 text-xs">−{dropPct}%</span>
                )}
                <span className="font-medium">{step.sessions} sessions</span>
                <span className="text-gray-400 text-xs">{step.rate_vs_first}%</span>
              </span>
            </div>
            <div className="h-6 bg-gray-100 rounded-md overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 flex items-center px-2 text-xs text-white"
                style={{ width: `${Math.max(widthPct, 2)}%`, minWidth: step.sessions > 0 ? '24px' : 0 }}
              >
                {step.sessions > 0 && widthPct > 10 && (
                  <span>{step.sessions}</span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TrafficChart({ data }) {
  const maxViews = Math.max(...data.map((d) => d.views), 1);
  const points = data.slice(-30);
  return (
    <div className="flex items-end gap-1 h-40 pt-4">
      {points.map((d) => {
        const h = (d.views / maxViews) * 100;
        return (
          <div
            key={d.date}
            className="flex-1 group relative flex flex-col-reverse"
            title={`${d.date} • ${d.views} views • ${d.sessions} sessions`}
          >
            <div
              className="w-full bg-emerald-500 rounded-t-sm hover:bg-emerald-600 transition-colors"
              style={{ height: `${h}%`, minHeight: d.views > 0 ? '2px' : 0 }}
            />
          </div>
        );
      })}
    </div>
  );
}
